// Payment readiness — Paystack primary, Hubtel failover. The frontend never
// holds keys; geo-api initializes each transaction server-side and returns
// an authorization URL. Failover is automatic at the API layer; the client
// calls POST /payments; geo-api remains authoritative for gateway selection.
import { geoApi } from '@/lib/geoApi';

export type PaymentProvider = 'paystack' | 'hubtel';
export type PaymentStatus = 'pending' | 'success' | 'failed' | 'cancelled' | string;

export interface InitializePaymentPayload {
  amount: number;
  customerEmail: string;
  currency: 'GHS' | 'NGN' | 'USD';
  invoiceId?: string;
  investorId?: string;
  preferredGateway?: PaymentProvider;
  description?: string;
  idempotencyKey?: string;
  callbackUrl?: string;
  metadata?: Record<string, unknown>;
}

export interface InitializedPayment {
  payment: {
    id: string;
    reference: string;
    amount: number;
    currency: string;
    status: PaymentStatus;
    provider?: PaymentProvider;
    invoice_id?: string;
    investor_id?: string;
  };
  authorizationUrl: string;
  deduped?: boolean;
}

export interface PaymentRecord {
  reference: string;
  provider: PaymentProvider;
  amount: number;
  currency: string;
  status: PaymentStatus;
  purpose?: string;
  createdAt: string;
}

export interface InvoiceLine {
  description: string;
  quantity: number;
  unit_amount: number;
  line_total: number;
  tax_amount?: number;
  discount_amount?: number;
}

export interface Invoice {
  id: string;
  number: string;
  issuedAt: string;
  dueAt?: string;
  currency: 'GHS' | 'NGN' | 'USD' | string;
  subtotal: number;
  tax: number;
  total: number;
  status: 'draft' | 'issued' | 'paid' | 'overdue' | string;
  payerName?: string;
  payerOrganization?: string;
  lines: InvoiceLine[];
  purpose?: string;
  /** Outstanding balance from the API (balance_due); undefined when not provided. */
  balanceDue?: number;
}

/* ── Manual payment evidence (Geo-API RC2, R11P P0.6-NITA-02A) ── */

/** Returned by POST /manual-payments/proof. */
export interface UploadedProof {
  id: string;
  originalName: string;
  contentType: string;
  byteSize: number;
  checksumSha256: string;
  uploadedAt: string;
}

/** A manual-payment submission as returned by the API (snake_case row). */
export interface ManualPaymentSubmission {
  id: string;
  invoice_id: string;
  payment_reference: string;
  provider_txn_ref?: string | null;
  amount_claimed: number | string;
  currency: string;
  payment_datetime?: string | null;
  status: 'pending_verification' | 'confirmed' | 'rejected' | string;
  rejection_reason?: string | null;
  verified_at?: string | null;
  created_at: string;
}

export interface ManualPaymentInstructions {
  accountName: string | null;
  accountNumber: string | null;
  network: string | null;
  instructions: string;
  currency: string;
  configured: boolean;
}

export interface ManualPaymentSubmitPayload {
  invoiceId: string;
  amountClaimed: number;
  proofFileId: string;
  providerTxnRef?: string;
  payerName?: string;
  payerPhone?: string;
  paymentDatetime?: string;
  applicantNote?: string;
}

export interface Receipt {
  id: string;
  reference: string;
  issuedAt: string;
  amount: number;
  currency: string;
  downloadUrl?: string;
}

export interface PaymentReminder {
  id: string;
  invoiceId?: string;
  title: string;
  dueAt: string;
  amount: number;
  currency: string;
  kind: 'renewal' | 'invoice' | 'subscription' | string;
}

interface RawPaymentRecord {
  id?: string;
  reference?: string;
  provider?: PaymentProvider;
  amount?: number | string;
  currency?: string;
  status?: PaymentStatus;
  purpose?: string;
  created_at?: string;
  createdAt?: string;
}

interface RawInvoiceLine {
  description?: string;
  quantity?: number | string;
  unit_amount?: number | string;
  line_total?: number | string;
  tax_amount?: number | string;
  discount_amount?: number | string;
}

interface RawInvoice {
  id?: string;
  invoice_number?: string;
  number?: string;
  issued_at?: string;
  issuedAt?: string;
  due_at?: string | null;
  dueAt?: string | null;
  currency?: string;
  subtotal?: number | string;
  tax_total?: number | string;
  tax?: number | string;
  total?: number | string;
  balance_due?: number | string;
  status?: string;
  payer_name?: string;
  payerName?: string;
  payer_organization?: string;
  payerOrganization?: string;
  items?: RawInvoiceLine[];
  lines?: RawInvoiceLine[];
  purpose?: string;
}

interface RawReceipt {
  id?: string;
  reference?: string;
  issued_at?: string;
  issuedAt?: string;
  amount?: number | string;
  currency?: string;
}

const numberValue = (value: number | string | null | undefined): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizePaymentStatus = (status?: string): PaymentStatus => {
  if (status === 'succeeded') return 'success';

  if (status === 'failed' || status === 'cancelled') {
    return status;
  }

  if (
    status === 'initiated' ||
    status === 'processing' ||
    status === 'pending'
  ) {
    return 'pending';
  }

  return status || 'pending';
};

const normalizePayment = (row: RawPaymentRecord): PaymentRecord => ({
  reference: String(row.reference ?? ''),
  provider: (row.provider ?? 'paystack') as PaymentProvider,
  amount: numberValue(row.amount),
  currency: String(row.currency ?? 'GHS'),
  status: normalizePaymentStatus(row.status),
  purpose: row.purpose,
  createdAt: String(row.createdAt ?? row.created_at ?? ''),
});

const normalizeInvoiceLine = (row: RawInvoiceLine): InvoiceLine => ({
  description: String(row.description ?? ''),
  quantity: numberValue(row.quantity),
  unit_amount: numberValue(row.unit_amount),
  line_total: numberValue(
    row.line_total ??
    (numberValue(row.unit_amount) * numberValue(row.quantity))
  ),
  tax_amount:
    row.tax_amount === undefined ? undefined : numberValue(row.tax_amount),
  discount_amount:
    row.discount_amount === undefined ? undefined : numberValue(row.discount_amount),
});

const normalizeInvoice = (row: RawInvoice): Invoice => ({
  id: String(row.id ?? ''),
  number: String(row.number ?? row.invoice_number ?? ''),
  issuedAt: String(row.issuedAt ?? row.issued_at ?? ''),
  dueAt: row.dueAt ?? row.due_at ?? undefined,
  currency: String(row.currency ?? 'GHS'),
  subtotal: numberValue(row.subtotal),
  tax: numberValue(row.tax ?? row.tax_total),
  total: numberValue(row.total),
  status: String(row.status ?? 'draft'),
  payerName: row.payerName ?? row.payer_name,
  payerOrganization: row.payerOrganization ?? row.payer_organization,
  lines: (row.lines ?? row.items ?? []).map(normalizeInvoiceLine),
  purpose: row.purpose,
  balanceDue: row.balance_due == null ? undefined : numberValue(row.balance_due),
});

const normalizeReceipt = (row: RawReceipt): Receipt => ({
  id: String(row.id ?? ''),
  reference: String(row.reference ?? ''),
  issuedAt: String(row.issuedAt ?? row.issued_at ?? ''),
  amount: numberValue(row.amount),
  currency: String(row.currency ?? 'GHS'),
});

type RawListEnvelope<T> = {
  data?: T[];
  items?: T[];
  count?: number;
};

const listRows = <T>(
  body: T[] | RawListEnvelope<T> | null | undefined
): T[] => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.items)) return body.items;
  return [];
};

export const paymentService = {
  async initialize(payload: InitializePaymentPayload) {
    const result = await geoApi.post<{
      payment?: {
        id?: string;
        reference?: string;
        amount?: number | string;
        currency?: string;
        status?: string;
        authorization_url?: string;
        primary_gateway?: PaymentProvider;
        active_gateway?: PaymentProvider;
        invoice_id?: string;
        investor_id?: string;
      };
      authorizationUrl?: string;
      deduped?: boolean;
    }>('/payments', payload);

    const row = result.data?.payment;

    return {
      ...result,
      data: row
        ? {
            payment: {
              id: String(row.id ?? ''),
              reference: String(row.reference ?? ''),
              amount: numberValue(row.amount),
              currency: String(row.currency ?? 'GHS'),
              status: normalizePaymentStatus(row.status),
              provider: row.active_gateway ?? row.primary_gateway,
              invoice_id: row.invoice_id,
              investor_id: row.investor_id,
            },
            authorizationUrl:
              result.data?.authorizationUrl ??
              row.authorization_url ??
              '',
            deduped: result.data?.deduped,
          }
        : null,
    };
  },
  async verify(paymentId: string) {
    const result = await geoApi.post<{
      payment?: RawPaymentRecord;
      receipt?: RawReceipt | null;
      alreadySucceeded?: boolean;
    }>(
      `/payments/${encodeURIComponent(paymentId)}/verify`
    );

    return {
      ...result,
      data: result.data?.payment
        ? {
            payment: normalizePayment(result.data.payment),
            receipt: result.data.receipt
              ? normalizeReceipt(result.data.receipt)
              : null,
            alreadySucceeded: result.data.alreadySucceeded,
          }
        : null,
    };
  },
  async history() {
    const result =
      await geoApi.get<RawPaymentRecord[] | RawListEnvelope<RawPaymentRecord>>(
        '/payments/me'
      );

    return {
      ...result,
      data: listRows(result.data).map(normalizePayment),
    };
  },
  async listInvoices() {
    const result =
      await geoApi.get<RawInvoice[] | RawListEnvelope<RawInvoice>>(
        '/invoices/me'
      );

    return {
      ...result,
      data: listRows(result.data).map(normalizeInvoice),
    };
  },
  async getInvoice(id: string) {
    const result = await geoApi.get<RawInvoice>(
      `/invoices/${encodeURIComponent(id)}`
    );
    return {
      ...result,
      data: result.data ? normalizeInvoice(result.data) : null,
    };
  },
  async listReceipts() {
    const result =
      await geoApi.get<RawReceipt[] | RawListEnvelope<RawReceipt>>('/receipts');

    return {
      ...result,
      data: listRows(result.data).map(normalizeReceipt),
    };
  },

  listReminders() {
    return Promise.resolve({ data: [] as PaymentReminder[] });
  },

  /* ── Manual payment evidence (Geo-API RC2) ── */

  /** Step 1: store the ACTUAL proof bytes. Returns { file: { id, ... } }. */
  uploadProof(file: File) {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return geoApi.postForm<{ file: UploadedProof }>('/manual-payments/proof', fd);
  },

  /** Step 2: submit the evidence for Finance verification (proofFileId required). */
  submitManualPayment(payload: ManualPaymentSubmitPayload) {
    return geoApi.post<{ submission: ManualPaymentSubmission; reference: string }>('/manual-payments', payload);
  },

  /** Configured payment instructions (account details are null until Finance configures them). */
  getManualPaymentInstructions() {
    return geoApi.get<ManualPaymentInstructions>('/manual-payments/instructions');
  },

  /** The investor's own submissions — authoritative status. */
  async listMyManualPayments() {
    const result = await geoApi.get<{ items?: ManualPaymentSubmission[]; count?: number }>('/manual-payments/me');
    return { ...result, data: Array.isArray(result.data?.items) ? result.data!.items! : (result.error ? null : []) };
  },
};
